import { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { PASSWORD_HINT, strongPasswordSchema } from "../../utils/passwordRules";
import { isValidPhoneNumber } from "libphonenumber-js";
import { FaGoogle } from "react-icons/fa";
import { Link, useNavigate, useParams } from "react-router-dom";
import { TermsModal } from "../../components/TermsModal";
import { handleSocialLogin } from "./SocialProvider";
import { googleProvider } from "../../firebase";
import { motion } from "framer-motion";
import axiosToken from "../../api/axiosClient";
import { toast } from "react-toastify";
import logo200 from "../../assets/logo/logo-200.png";

const RegisterPage = () => {
  const navigate = useNavigate();
  const { callback } = useParams(); // récupère callback dynamique depuis l'URL

  const [loading, setLoading] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [socialRegister, setSocialRegister] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState(1);
  const nomRef = useRef(null);
  const phoneInputRef = useRef(null);

  const validatePhone = (value) =>
    isValidPhoneNumber(value || "") ? true : false;

  const formik = useFormik({
    initialValues: {
      nom: "",
      prenom: "",
      email: "",
      password: "",
      // confirmPassword: "",
      telephone: "",
      // whatsapp: "",
      acceptTerms: false,
      sameAsPhone: false,
    },

    validationSchema: Yup.object({
      nom: Yup.string().required("Nom requis"),
      prenom: Yup.string().required("Prénom requis"),
      email: Yup.string().email("Email invalide").required("Email requis"),
      password: strongPasswordSchema,
      // confirmPassword: Yup.string()
      //   .oneOf([Yup.ref("password")], "Les mots de passe ne correspondent pas")
      //   .required("Confirmation requise"),

      telephone: Yup.string()
        .required("Téléphone requis")
        .test("is-valid-phone", "Numéro de téléphone invalide", validatePhone),

      // whatsapp: Yup.string()
      //   .nullable()
      //   .test("is-valid-whatsapp", "Numéro WhatsApp invalide", (value) => {
      //     if (!value || value.trim() === "") return true; // Accepte les valeurs nulles ou vides
      //     return validatePhone(value)
      //   }),

      acceptTerms: Yup.boolean().oneOf(
        [true],
        "Vous devez lire et accepter les conditions"
      ),
    }),

    onSubmit: async (values) => {
      setLoading(true);
      // if (step === 1) {
      //   setStep(2);
      //   nomRef.current?.blur();
      //   phoneInputRef.current?.focus();
      //   setLoading(false)
      //   return;
      // }

      // console.log("Formulaire soumis :", values);
      // Appel API pour l'inscription
      try {
        const response = await axiosToken.post("/user/register", values);

        // Gérer la réponse, par exemple stocker le token
        localStorage.setItem("authToken", response.data.token);
        localStorage.setItem("email", values.email);

        toast.success("Inscription réussie !", { position: "top-right" });

        setLoading(false);
        setTimeout(() => {
          navigate(`/register-2/login/${callback || ""}`); // rediriger vers la page de vérification ou autre
        }, 1000);

        return response.data;
      } catch (error) {
        toast.error(
          error?.response?.data?.error ||
            "Erreur lors de l'inscription. Veuillez réessayer.",
          { position: "bottom-left" }
        );
        console.error("Erreur lors de la récupération des services:", error);
        setLoading(false);
        return [];
      }
    },
  });

  useEffect(() => {
    if (formik.values.sameAsPhone) {
      formik.setFieldValue("whatsapp", formik.values.telephone);
    }
  }, [formik.values.telephone, formik.values.sameAsPhone]);

  document.title = "Inscription || Barberlys";

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full bg-white max-w-xl shadow-xl p-8">
        {/* Header */}
        <div className="mb-6 text-center">
          {/* <span className="text-sm bg-secondary-50 text-secondary-700 px-3 py-1 font-medium">
            Inscription
          </span> */}
          <Link to={`/index`} className="cursor-pointer">
            <img src={logo200} className="m-auto" />
          </Link>
          {/* <h2 className="text-2xl font-semibold mt-3">Créez votre compte</h2> */}
        </div>

        {/* Social Buttons */}
        <div className="flex justify-center gap-4 mb-6">
          <button
            onClick={async () => {
              setShowPassword(false);
              setShowConfirmPassword(false);
              const dataGoogle = await handleSocialLogin(googleProvider);

              const uid = dataGoogle.uid;
              formik.setFieldValue("password", `${uid}@gmail.com` || "");
              formik.setFieldValue("confirmPassword", `${uid}@gmail.com` || "");
              setSocialRegister(true);

              if (dataGoogle?.providerData) {
                const { displayName, email, phoneNumber } =
                  dataGoogle.providerData[0];

                // Séparation nom et prénom si possible
                const [prenom, nom] = displayName?.split(" ") ?? ["", ""];

                formik.setFieldValue("nom", nom ? nom.toUpperCase() : "");
                formik.setFieldValue("prenom", prenom || "");
                formik.setFieldValue("email", email || "");
                formik.setFieldValue("telephone", phoneNumber || "");

                // Tu peux aussi désactiver les champs ou les rendre readonly si besoin
              }
            }}
            className="bg-[#1a73e8] text-white p-3 hover:scale-105 transition flex gap-2 items-center"
          >
            <FaGoogle size={20} />
            <span>S'inscrire avec Google</span>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={formik.handleSubmit}>
          <motion.div
            key="step1"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 50 }}
            transition={{ duration: 0.4 }}
          >
            <div className="space-y-5">
              <div className={`flex justify-between`}>
                <div className="space-y-1">
                  <input
                    ref={nomRef}
                    type="text"
                    name="nom"
                    placeholder="Nom"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.nom}
                    className="w-full placeholder-gray-400 px-4 py-2 shadow focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                  {formik.touched.nom && formik.errors.nom && (
                    <p className="text-red-400 text-sm">{formik.errors.nom}</p>
                  )}
                </div>

                <div className="space-y-1">
                  <input
                    type="text"
                    name="prenom"
                    placeholder="Prénom"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.prenom}
                    className="w-full placeholder-gray-400 px-4 py-2 shadow focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                  {formik.touched.prenom && formik.errors.prenom && (
                    <p className="text-red-400 text-sm">
                      {formik.errors.prenom}
                    </p>
                  )}
                </div>
              </div>

              {/* Téléphone */}
              <div className="space-y-1">
                <input
                  ref={phoneInputRef}
                  type="tel"
                  name="telephone"
                  placeholder="Numéro de téléphone"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.telephone}
                  className="w-full placeholder-gray-400 px-4 py-2 shadow focus:outline-none focus:ring-2 focus:ring-gray-500"
                />

                {formik.touched.telephone && formik.errors.telephone && (
                  <p className="text-red-400 text-sm">
                    {formik.errors.telephone}
                  </p>
                )}
                <p className="text-xs text-gray-700">Ex : +2290141414141</p>
              </div>

              <div className="space-y-1">
                <input
                  type="email"
                  name="email"
                  placeholder="Adresse email"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  className="w-full placeholder-gray-400 px-4 py-2 shadow focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
                {formik.touched.email && formik.errors.email && (
                  <p className="text-red-400 text-sm">{formik.errors.email}</p>
                )}
              </div>

              {!socialRegister && (
                <>
                  <div className="relative space-y-1">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Mot de passe"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.password}
                      className="w-full placeholder-gray-400 px-4 py-2 shadow focus:outline-none focus:ring-2 focus:ring-gray-500"
                      readOnly={socialRegister}
                    />
                    <button
                      disabled={socialRegister}
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-2 text-sm text-gray-300"
                    >
                      {showPassword ? "Cacher" : "Afficher"}
                    </button>
                    {formik.touched.password && formik.errors.password && (
                      <p className="text-red-400 text-sm">
                        {formik.errors.password}
                      </p>
                    )}
                    <p className="text-gray-500 text-xs">{PASSWORD_HINT}</p>
                  </div>

                  {/* <div className="relative space-y-1">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      placeholder="Confirmer le mot de passe"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.confirmPassword}
                      className="w-full bg-white/20 text-white placeholder-gray-400 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-white"
                      readOnly={socialRegister}
                    />
                    <button
                      disabled={socialRegister}
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-3 top-2 text-sm text-gray-300"
                    >
                      {showConfirmPassword ? "Cacher" : "Afficher"}
                    </button>
                    {formik.touched.confirmPassword &&
                      formik.errors.confirmPassword && (
                        <p className="text-red-400 text-sm">
                          {formik.errors.confirmPassword}
                        </p>
                      )}
                  </div> */}
                </>
              )}

              {/* <label className="flex items-center text-sm text-gray-300 gap-2 user-select-none ">
                <input
                  type="checkbox"
                  name="sameAsPhone"
                  checked={formik.values.sameAsPhone}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    formik.setFieldValue("sameAsPhone", checked);
                    if (checked) {
                      formik.setFieldValue("whatsapp", formik.values.telephone);
                    }
                  }}
                  className="w-4 h-4 accent-secondary-500"
                />
                Utiliser le même numéro pour WhatsApp
              </label> */}

              {/* WhatsApp */}
              {/* <div className="space-y-1">
                <input
                  type="tel"
                  name="whatsapp"
                  placeholder="Numéro WhatsApp (facultatif)"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.whatsapp}
                  className="w-full bg-white/20 text-white placeholder-gray-400 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-white"
                />
                {formik.touched.whatsapp && formik.errors.whatsapp && (
                  <p className="text-red-400 text-sm">
                    {formik.errors.whatsapp}
                  </p>
                )}
                <p className="text-xs text-gray-400">Ex : +2290141414141</p>
              </div> */}

              {/* Conditions et politiques */}
              <label className="flex items-center text-sm gap-2 mt-4 user-select-none">
                <input
                  type="checkbox"
                  name="acceptTerms"
                  checked={formik.values.acceptTerms}
                  onChange={formik.handleChange}
                  className="w-4 h-4 accent-secondary-500"
                />
                J'accepte les{" "}
                <span
                  className="font-semibold underline cursor-pointer"
                  onClick={() => setShowTerms(true)}
                >
                  conditions générales et la politique de confidentialité
                </span>
              </label>
              {formik.touched.acceptTerms && formik.errors.acceptTerms && (
                <p className="text-red-400 text-sm">
                  {formik.errors.acceptTerms}
                </p>
              )}

              <button
                disabled={!(formik.isValid && formik.dirty) || loading}
                type="submit"
                className={`w-full bg-secondary-300 ${
                  !(formik.isValid && formik.dirty)
                    ? ""
                    : " hover:bg-secondary-400"
                } text-white py-2 font-semibold transition-all`}
              >
                {loading ? (
                  <div className="flex flex-row justify-center items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-t-4 border-b-4 border-white" />
                    <span>Inscription...</span>
                  </div>
                ) : (
                  "S'inscrire"
                )}
              </button>
            </div>
          </motion.div>

          {/* Lien vers la page de connexion */}

          <p className="text-center text-sm mt-4">
            Déjà inscrit ?{" "}
            <Link
              to="/login"
              className="text-secondary-500 hover:text-secondary-800"
            >
              Se connecter
            </Link>
          </p>
        </form>
      </div>

      {/* Modal */}
      {showTerms && <TermsModal onClose={() => setShowTerms(false)} />}
    </div>
  );
};

export default RegisterPage;
